import { AccountingSupplier, Role, Team, TeamType, User } from '@modules/Auth/models/Auth';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// 🎯 Tipo para settings del usuario (estructura desde /me)
export type UserSettings = Record<
  string,
  {
    name: string;
    value: {
      value: any;
    };
    is_active?: boolean;
  }
>;

function readSettingBool(settings: UserSettings | null, key: string): boolean {
  if (!settings) return false;
  const v = settings[key]?.value?.value ?? settings[key]?.value;
  return v === true || v === 'true';
}

interface AuthState {
  user: User | null;
  team: Team | null;
  role: Role | null;
  token: string | null;
  isAuthenticated: boolean;
  settings: UserSettings | null; // 🎯 Settings del usuario desde /me

  // 🎯 Estado de impersonación
  originalToken: string | null;
  originalUser: User | null;
  originalTeam: Team | null;
  originalRole: Role | null;
  originalSettings: UserSettings | null;
  isImpersonating: boolean;
  impersonationLogId: number | null;

  setUser: (user: User | null) => void;
  setTeam: (team: Team | null) => void;
  setRole: (role: Role | null) => void;
  setSettings: (settings: UserSettings | null) => void;
  setToken: (token: string | null) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  logout: () => void;

  // 🎯 Métodos de impersonación
  startImpersonation: (newToken: string, newUser: User, newTeam: Team, newRole: Role, logId: number) => void;
  stopImpersonation: () => void;

  // 🎯 Métodos de permisos
  getTeamType: () => TeamType | null;
  getUserPermissions: () => string[];
  hasPermission: (slug: string) => boolean;
  hasAnyPermission: (slugs: string[]) => boolean;
  hasAllPermissions: (slugs: string[]) => boolean;
  canAccess: (allowedTeamTypes?: TeamType[], requiredPermissions?: string[]) => boolean;
  isAdminRole: () => boolean;
  // 🎯 Verificar si wallet_visible está habilitado (para tenant_external)
  isWalletVisible: () => boolean;
  /** Proveedor contable del team actual (desde /me). */
  getAccountingSupplier: () => AccountingSupplier | null;
  /** true si el tenant usa integración Holded. */
  isHoldedAccounting: () => boolean;
  /** true si el tenant usa integración Contalink. */
  isContalinkAccounting: () => boolean;
  /** Rentabilidad en la tienda hija: `profitability.enabled` (tenant_external) */
  getProfitabilityEnabled: () => boolean;
  /**
   * Menú / rutas de rentabilidad: external si su `profitability.enabled`;
   * padre 3PL solo si el backend indica que algún hijo tiene el módulo (p. ej. `profitability.children_enabled`).
   */
  getProfitabilityMenuVisible: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      team: null,
      role: null,
      token: null,
      isAuthenticated: false,

      // 🎯 Estado inicial de impersonación
      originalToken: null,
      originalUser: null,
      originalTeam: null,
      originalRole: null,
      originalSettings: null,
      isImpersonating: false,
      impersonationLogId: null,
      settings: null,

      setUser: (user) => set({ user }),
      setTeam: (team) => set({ team }),
      setRole: (role) => set({ role }),
      setSettings: (settings) => set({ settings }),
      setToken: (token) => set({ token }),
      setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),

      logout: () =>
        set({
          user: null,
          team: null,
          role: null,
          settings: null,
          token: null,
          isAuthenticated: false,
          originalToken: null,
          originalUser: null,
          originalTeam: null,
          originalRole: null,
          originalSettings: null,
          isImpersonating: false,
          impersonationLogId: null,
        }),

      // 🎯 Iniciar impersonación
      startImpersonation: (newToken, newUser, newTeam, newRole, logId) => {
        const currentState = get();
        set({
          originalToken: currentState.token,
          originalUser: currentState.user,
          originalTeam: currentState.team,
          originalRole: currentState.role,
          originalSettings: currentState.settings,
          isImpersonating: true,
          impersonationLogId: logId,
          token: newToken,
          user: newUser,
          team: newTeam,
          role: newRole,
          settings: null, // Los settings se actualizarán cuando llegue la respuesta de /me
        });
      },

      // 🎯 Detener impersonación
      stopImpersonation: () => {
        const currentState = get();
        set({
          token: currentState.originalToken,
          user: currentState.originalUser,
          team: currentState.originalTeam,
          role: currentState.originalRole,
          settings: currentState.originalSettings,
          originalToken: null,
          originalUser: null,
          originalTeam: null,
          originalRole: null,
          originalSettings: null,
          isImpersonating: false,
          impersonationLogId: null,
        });
      },

      // 🎯 Obtener el tipo de team del usuario
      getTeamType: () => {
        const { team } = get();
        return team?.type ?? null;
      },

      // 🎯 Obtener array de slugs de permisos del usuario
      getUserPermissions: () => {
        const { role } = get();
        if (!role || !role.permissions) return [];
        return role.permissions.map((p) => p.slug);
      },

      // 🎯 Verificar si el usuario tiene un permiso específico
      hasPermission: (slug: string) => {
        const { role } = get();
        if (!role || !role.permissions) return false;
        return role.permissions.some((p) => p.slug === slug);
      },

      // 🎯 Verificar si el usuario tiene al menos uno de los permisos
      hasAnyPermission: (slugs: string[]) => {
        if (slugs.length === 0) return true;
        const { role } = get();
        if (!role || !role.permissions) return false;
        return slugs.some((slug) => role.permissions.some((p) => p.slug === slug));
      },

      // 🎯 Verificar si el usuario tiene todos los permisos
      hasAllPermissions: (slugs: string[]) => {
        if (slugs.length === 0) return true;
        const { role } = get();
        if (!role || !role.permissions) return false;
        return slugs.every((slug) => role.permissions.some((p) => p.slug === slug));
      },

      // 🎯 Verificación combinada: team_type + permisos
      canAccess: (allowedTeamTypes?: TeamType[], requiredPermissions?: string[]) => {
        const { team, role } = get();

        // Si no hay usuario autenticado, denegar acceso
        if (!team) return false;

        // 1. Verificar team_type (si se especificó)
        if (allowedTeamTypes && allowedTeamTypes.length > 0) {
          if (!allowedTeamTypes.includes(team.type)) {
            return false;
          }
        }

        // 2. Verificar permisos (si se especificaron)
        if (requiredPermissions && requiredPermissions.length > 0) {
          if (!role || !role.permissions) return false;
          // Por defecto, requiere al menos uno de los permisos
          return requiredPermissions.some((slug) => role.permissions.some((p) => p.slug === slug));
        }

        // Si no hay restricciones de permisos, permitir acceso
        return true;
      },

      // 🎯 Verificar si el usuario tiene rol de admin
      isAdminRole: () => {
        const { role } = get();
        if (!role) return false;
        return role.name.toLowerCase() === 'admin';
      },

      getAccountingSupplier: () => {
        const { team } = get();
        return team?.accounting_supplier ?? null;
      },

      isHoldedAccounting: () => get().getAccountingSupplier() === 'holded',

      isContalinkAccounting: () => get().getAccountingSupplier() === 'contalink',

      // 🎯 Verificar si wallet_visible está habilitado (para tenant_external)
      // Para tenant y tenant_3pl siempre retorna true
      // Para tenant_external, depende de beeping_team.wallet_visible
      isWalletVisible: () => {
        const { team } = get();
        if (!team) return false;

        // Para tenant y tenant_3pl, la wallet siempre está visible
        if (team.type === 'tenant' || team.type === 'tenant_3pl') {
          return true;
        }

        // Para tenant_external, verificar beeping_team.wallet_visible
        if (team.type === 'tenant_external') {
          return team.beeping_team?.wallet_visible ?? false;
        }

        return false;
      },

      // 🎯 Obtener un setting específico
      getSetting: (key: string) => {
        const { settings } = get();
        if (!settings) return null;
        return settings[key]?.value?.value ?? null;
      },

      // 🎯 Verificar si CRM está habilitado
      getCRMEnabled: () => {
        const { settings } = get();
        if (!settings) return false;
        const crmEnabled = settings['crm.enabled']?.value?.value ?? settings['crm.enabled']?.value;
        return crmEnabled === true || crmEnabled === 'true';
      },

      getProfitabilityEnabled: () => {
        const { settings } = get();
        return readSettingBool(settings, 'profitability.enabled');
      },

      getProfitabilityMenuVisible: () => {
        const { settings } = get();
        const teamType = get().getTeamType();
        if (!settings || !teamType) return false;

        if (teamType === 'tenant_external') {
          return readSettingBool(settings, 'profitability.enabled');
        }

        if (teamType === 'tenant_3pl') {
          if (readSettingBool(settings, 'profitability.children_enabled')) return true;
          if (readSettingBool(settings, 'profitability.child_enabled')) return true;
          if (readSettingBool(settings, 'profitability.any_child_enabled')) return true;
          return readSettingBool(settings, 'profitability.enabled');
        }

        return false;
      },

      // 🎯 Obtener quién gestiona el CRM
      getCRMManagedBy: () => {
        const { settings } = get();
        if (!settings) return null;
        const value = settings['crm.managed_by']?.value?.value ?? settings['crm.managed_by']?.value;
        if (value === 'tenant_external' || value === 'tenant_3pl') {
          return value;
        }
        return null;
      },

      // 🎯 Verificar si el usuario puede gestionar CRM
      canManageCRM: () => {
        const crmManagedBy = get().getCRMManagedBy();
        const teamType = get().getTeamType();

        // Si es tenant_external y crm.managed_by es tenant_3pl, no puede gestionar
        return !(teamType === 'tenant_external' && crmManagedBy === 'tenant_3pl');
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        team: state.team,
        role: state.role,
        settings: state.settings,
        originalToken: state.originalToken,
        originalUser: state.originalUser,
        originalTeam: state.originalTeam,
        originalRole: state.originalRole,
        originalSettings: state.originalSettings,
        isImpersonating: state.isImpersonating,
        impersonationLogId: state.impersonationLogId,
      }),
    },
  ),
);
